import type { UniversalCamera } from "@babylonjs/core/Cameras/universalCamera";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Scene } from "@babylonjs/core/scene";
import { WeaponSpriteGenerator } from "../assets/WeaponSpriteGenerator";
import { SeededRandom } from "../core/SeededRandom";
import type { InputManager } from "../core/InputManager";
import type { GameState } from "../game/GameState";
import type { ParticleFactory } from "../rendering/ParticleFactory";
import type { AudioSystem } from "../systems/AudioSystem";
import type { Hud } from "../ui/Hud";
import {
  createAutoAimedHitscanRays,
  createHitscanRays,
  findVerticalAutoAimDirection,
  pickHitscan,
} from "../weapons/HitscanSystem";
import { WEAPON_CONFIGS, type WeaponKind } from "../weapons/Weapon";
import type { PlayerController } from "./PlayerController";

export interface CombatCallbacks {
  readonly onEnemyHit: (
    enemyId: string,
    damage: number,
    hitPoint: Vector3,
    direction: Vector3,
  ) => void;
  readonly onLoudShot: (position: Vector3) => void;
}

export class PlayerCombat {
  private cooldown = 0;
  private reloadRemaining = 0;
  private recoil = 0;
  private weaponTime = 0;
  private shotCounter = 0;
  private readonly sprites = new WeaponSpriteGenerator();
  private verticalAutoAim = false;

  public constructor(
    private readonly scene: Scene,
    private readonly camera: UniversalCamera,
    private readonly controller: PlayerController,
    private readonly input: InputManager,
    private readonly state: GameState,
    private readonly hud: Hud,
    private readonly particles: ParticleFactory,
    private readonly audio: AudioSystem,
    private readonly callbacks: CombatCallbacks,
  ) {}

  public update(deltaSeconds: number, enabled: boolean): void {
    this.cooldown = Math.max(0, this.cooldown - deltaSeconds);
    this.weaponTime += deltaSeconds;
    this.recoil = Math.max(0, this.recoil - deltaSeconds * 4.8);
    if (this.reloadRemaining > 0) {
      this.reloadRemaining -= deltaSeconds;
      if (this.reloadRemaining <= 0) this.finishReload();
    }

    if (enabled) {
      this.handleWeaponSelection();
      if (this.input.consumePressed("KeyR")) this.beginReload();
      if (this.input.consumeFirePressed() || this.input.isFireHeld) this.tryFire();
    }
    this.drawWeapon();
  }

  public setVerticalAutoAim(enabled: boolean): void {
    this.verticalAutoAim = enabled;
  }

  private handleWeaponSelection(): void {
    if (this.input.consumePressed("Digit1")) this.selectWeapon("pistol");
    if (this.input.consumePressed("Digit2")) this.selectWeapon("shotgun");
    if (this.input.consumePressed("Digit3")) this.selectWeapon("rifle");
    const switchPressed = this.input.consumePressed("SwitchWeapon");
    const wheel = this.input.consumeWheelDirection();
    if (switchPressed || wheel !== 0) {
      const owned = this.state.ownedWeapons;
      const index = owned.indexOf(this.state.selectedWeapon);
      this.selectWeapon(owned[(index + (wheel < 0 ? -1 : 1) + owned.length) % owned.length]!);
    }
  }

  private selectWeapon(weapon: WeaponKind): void {
    if (!this.state.ownedWeapons.includes(weapon) || weapon === this.state.selectedWeapon) return;
    this.state.selectedWeapon = weapon;
    this.reloadRemaining = 0;
  }

  private tryFire(): void {
    if (this.cooldown > 0 || this.reloadRemaining > 0) return;
    const weapon = this.state.selectedWeapon;
    const config = WEAPON_CONFIGS[weapon];
    const ammo = this.state.ammo[weapon];
    if (ammo.magazine <= 0) {
      this.audio.play("dry_fire");
      this.cooldown = 0.2;
      return;
    }
    ammo.magazine -= 1;
    this.cooldown = config.fireInterval;
    this.recoil = weapon === "shotgun" ? 1 : 0.45;
    this.controller.addRecoil(weapon === "shotgun" ? 0.045 : 0.012);
    if (weapon === "shotgun") this.controller.shake(0.2, 0.018);
    this.hud.flashMuzzle();
    this.audio.play(
      weapon === "shotgun" ? "shotgun_fire" : weapon === "rifle" ? "rifle_fire" : "pistol_fire",
    );
    this.callbacks.onLoudShot(this.camera.position);

    const random = new SeededRandom(`${this.state.seed}:shot:${this.shotCounter}`);
    this.shotCounter += 1;
    const forward = this.camera.getDirection(Vector3.Forward());
    const autoAimDirection = this.verticalAutoAim
      ? findVerticalAutoAimDirection(this.scene, this.camera.position, forward, config.range)
      : undefined;
    const rays = autoAimDirection
      ? createAutoAimedHitscanRays(
          this.camera.position,
          forward,
          autoAimDirection,
          config.pellets,
          config.spreadRadians,
          config.range,
          random,
        )
      : createHitscanRays(
          this.camera.position,
          forward,
          config.pellets,
          config.spreadRadians,
          config.range,
          random,
        );
    for (const ray of rays) {
      const hit = pickHitscan(this.scene, ray);
      if (!hit?.hit || !hit.pickedPoint || !hit.pickedMesh) continue;
      const enemyId = (hit.pickedMesh.metadata as { enemyId?: string } | null)?.enemyId;
      if (enemyId) {
        this.callbacks.onEnemyHit(enemyId, config.damagePerPellet, hit.pickedPoint, ray.direction);
        this.particles.burst(hit.pickedPoint, ray.direction, true);
      } else {
        this.particles.burst(hit.pickedPoint, ray.direction, false);
        this.audio.play("wall_impact", hit.pickedPoint);
      }
    }
    if (ammo.magazine === 0 && ammo.reserve > 0) this.beginReload();
  }

  private beginReload(): void {
    const weapon = this.state.selectedWeapon;
    const config = WEAPON_CONFIGS[weapon];
    const ammo = this.state.ammo[weapon];
    if (ammo.magazine >= config.magazineSize || ammo.reserve <= 0 || this.reloadRemaining > 0)
      return;
    this.reloadRemaining = config.reloadDuration;
    this.audio.play(weapon === "shotgun" ? "shotgun_pump" : "weapon_reload");
  }

  private finishReload(): void {
    const weapon = this.state.selectedWeapon;
    const config = WEAPON_CONFIGS[weapon];
    const ammo = this.state.ammo[weapon];
    const needed = config.magazineSize - ammo.magazine;
    const transferred = Math.min(needed, ammo.reserve);
    ammo.magazine += transferred;
    ammo.reserve -= transferred;
  }

  private drawWeapon(): void {
    const canvas = this.hud.weaponCanvas;
    const context = canvas.getContext("2d");
    if (!context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.imageSmoothingEnabled = false;
    const sprite = this.sprites.get(this.state.selectedWeapon);
    const bobAmount = this.controller.isMoving ? 7 : 2;
    const bobX = Math.sin(this.weaponTime * 7) * bobAmount;
    const bobY = Math.abs(Math.cos(this.weaponTime * 7)) * bobAmount;
    const reloadDrop =
      this.reloadRemaining > 0 ? 55 * Math.sin(Math.PI * Math.min(1, this.reloadRemaining)) : 0;
    const recoilY = this.recoil * 24;
    const width = this.state.selectedWeapon === "pistol" ? 245 : 300;
    const height = (sprite.height / sprite.width) * width;
    context.drawImage(
      sprite,
      canvas.width / 2 - width / 2 + bobX,
      canvas.height - height + bobY + reloadDrop + recoilY,
      width,
      height,
    );
  }
}
